// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface MiniSignInProps {
    extendedStyles?: MiniSignInStyles;
}

type Props = MiniSignInProps;

interface MiniSignInStyles {
    container?: InjectableCss;
    createAccount?: {
        wrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        description?: TypographyPresentationProps;
        button?: ButtonPresentationProps;
    };
}

const miniSignInStyles: MiniSignInStyles = {
    container: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            ${xScreen("lte", "md")} {
                max-width: 620px;
                margin: auto;
            }
        `,
    },
    createAccount: {
        wrapper: {
            css: css`
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: flex-start;
                flex-direction: column;
                margin-top: 34px;
            `,
        },
        description: {
            size: 12,
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                margin-bottom: 16px;
            `,
        },
        button: {
            mergeCss: true,
            variant: "secondary",
            css: css`
                width: 100%;
            `,
        },
    },
};

const MiniSignIn: FC<Props> = ({ extendedStyles }) => {
    const { allowCreateAccount, createAccountUrl } = useAppSelector(state => {
        const { search } = getLocation(state);
        const accountSettings = getSettingsCollection(state).accountSettings;
        const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
        return {
            allowCreateAccount: accountSettings.allowCreateAccount,
            createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        };
    });
    const history = useHistory();
    const [styles] = useState(() => mergeWithCss(miniSignInStyles, extendedStyles));

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }
        history.push(createAccountUrl);
    };

    return (
        <StyledWrapper {...styles.container}>
            {createWidgetElement("SignIn/SignInExistingAccount", {
                fields: {
                    overrideTitle: "Log In",
                    signInButtonLabel: "Sign In",
                    isMiniSignIn: true,
                },
            })}
            {allowCreateAccount && createAccountUrl && (
                <StyledWrapper {...styles.createAccount?.wrapper}>
                    <Typography variant="h5" {...styles.createAccount?.title}>
                        {translate("Create An Account")}
                    </Typography>
                    <Typography {...styles.createAccount?.description}>
                        {translate("Welcome! It's quick and easy to set up an account.")}
                    </Typography>
                    <Button onClick={handleCreateNewAccount} {...styles.createAccount?.button}>
                        {translate("Create An Account")}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default MiniSignIn;
